import {
  LegacyPortalShippingService,
  LegacyPortalShippingStatusDispatchStatus,
} from "@spwn-portal/grpc";
import useSWRImmutable from "swr/immutable";
import { useConnectClient } from "utility/connectWeb";

import type { PromiseClient } from "@bufbuild/connect";
import type { FulfillmentData } from "pages/AccountSettlementHistoryOrderId/types/FulfillmentData";

type UseOrderFulfillmentInput = {
  /** 配送（出庫）情報のFirestore ID。配送のない注文はnull */
  fulfillmentFirestoreId: string | null;
  orderId: string | null;
};

type UseOrderFulfillmentOutput = {
  fulfillmentData: FulfillmentData | null;
  /**
   * 出庫に使う配送先住所ID。購入後に住所が変わると運用が出庫の住所IDだけを付け替えて発送するため、
   * 注文時の住所IDとは異なることがある。取得できない場合はnull
   */
  shippingUniqueId: string | null;
  isLoading: boolean;
  /**
   * 配送状況の取得に失敗したか。出庫の住所IDが未設定の場合と区別するため。
   * 通信エラーなどはSWRが再試行する。BigQueryの取得失敗はAPIが配送状況不明の正常レスポンスで返すため、ここには含まれない
   */
  isError: boolean;
};

type FetchedFulfillment = {
  fulfillmentData: FulfillmentData | null;
  shippingUniqueId: string | null;
};

const convertDispatchStatusToShippingStatus = (
  status: LegacyPortalShippingStatusDispatchStatus
): string => {
  switch (status) {
    case LegacyPortalShippingStatusDispatchStatus.PACKING:
      return "packing";
    case LegacyPortalShippingStatusDispatchStatus.SHIPPED:
      return "shipped";
    case LegacyPortalShippingStatusDispatchStatus.CANCELLED:
      return "cancelled";
    case LegacyPortalShippingStatusDispatchStatus.UNSPECIFIED:
    default:
      return "suspended";
  }
};

const fetchFulfillmentDataFromBigQuery = async (
  client: PromiseClient<typeof LegacyPortalShippingService>,
  fulfillmentFirestoreId: string
): Promise<FetchedFulfillment> => {
  // 通信エラーなどの失敗は握りつぶさずSWRに渡す。成功としてキャッシュされると再試行されず、
  // 呼び出し側も「出庫の住所IDが未設定」と区別できないため
  const response = await client.getShippingStatusByFulfillmentFirestoreId({
    fulfillmentFirestoreId,
  });
  const shippingUniqueId = response.shippingUniqueId ?? null;
  if (!response.shippingStatus) {
    return { fulfillmentData: null, shippingUniqueId };
  }

  const { status, trackingCode, trackingUrl, deliveryCarrier } =
    response.shippingStatus;

  return {
    fulfillmentData: {
      carrier: deliveryCarrier,
      trackingCode: trackingCode ?? "",
      shippingStatus: convertDispatchStatusToShippingStatus(status),
      url: trackingUrl ?? "",
    },
    shippingUniqueId,
  };
};

export const useOrderFulfillment = (
  input: UseOrderFulfillmentInput
): UseOrderFulfillmentOutput => {
  const { fulfillmentFirestoreId, orderId } = input;
  const client = useConnectClient(LegacyPortalShippingService);

  const isFetch =
    fulfillmentFirestoreId !== null && fulfillmentFirestoreId !== "" && client;
  const { data, error, isLoading } = useSWRImmutable<FetchedFulfillment, Error>(
    isFetch ? ["orderFulfillment", orderId, fulfillmentFirestoreId] : null,
    isFetch
      ? async () =>
          fetchFulfillmentDataFromBigQuery(client, fulfillmentFirestoreId)
      : null,
    {
      onError: (err) => {
        console.error("Failed to fetch fulfillment data from BigQuery:", err);
      },
    }
  );

  return {
    fulfillmentData: data?.fulfillmentData ?? null,
    shippingUniqueId: data?.shippingUniqueId ?? null,
    isLoading,
    isError: error !== undefined,
  };
};
